import React, { useState, useEffect, useMemo, useRef } from 'react';
import { View, TouchableOpacity, NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import MyAppText from '../MyAppText';
import { translate } from '../../services/translate';
import Map from '../Map';
import MyDataTable from '../MyDataTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp, faBuilding, faRoute, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { dispatchService, UnitCategoryType } from '../../services/central-api/dispatch';
import moment from 'moment';
import { MyCheckbox } from '../MyCheckbox';
import FilterInput from '../Filter/components/FilterInput';

import DisplacedModal from './DisplacedModal';
import Tooltip from '../Tooltip';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import OccurrenceUnitContent from './OccurrenceUnitContent';
import { WindowInformation } from '../../services/window-information';
import MapPolygonDropdown from '../MapPolygonDropdown';
import { Hoverable } from 'react-native-web-hover';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { OccurrenceDetailFilter } from './OccurrenceDetailFilter';
import { titleCasePt } from '../../services/title-case-pt';
import Badge, { BadgeColor } from '../Badge';
import { useQuery } from '@tanstack/react-query';
import { useIsFocused } from '@react-navigation/native';

export interface UserAndDistances extends User {
    distance?: number;
}

export interface UnitAndDistance extends Unit {
    distance?: number;
    isStarter: boolean;
    displaced: boolean;
    displaceReasonId?: number;
    policeStationId?: number;
}

interface DispatchTabParams {
    occurrence: Occurrence;
    pin?: LatLng;
    dispatchedUnits: Unit[];
    setDispatchedUnits: (value: Unit[]) => void;
    markerChanged?: (pos: LatLng) => void;
    area?: string;
}


export default function OccurrenceDetailsDispatchTab({ occurrence, pin, dispatchedUnits, setDispatchedUnits, markerChanged, area }: DispatchTabParams) {
    const { styles, theme } = useStyles(styleSheet);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const windowInfo = WindowInformation();

    const [, updateState] = React.useState({});
    const forceUpdate = React.useCallback(() => updateState({}), []);
    const [agentPositions, setAgentPositions] = useState<UnitMapPosition[]>([]);
    const [refetchUser, setRefetchUser] = useState<boolean>(false);
    const [unitsToDispatch, setUnitsToDispatch] = useState<Unit[]>([]);
    const [equipmentFilterList, setEquipmentFilterList] = useState<number[]>([]);
    const [skillFilterList, setSkillFilterList] = useState<number[]>([]);
    const [assignments, setAssignments] = useState<DropdownResource[]>([]);
    const [policeStationsPositions, setPoliceStationsPositions] = useState<PoliceStation[]>([]);
    const [unitFilter, setUnitFilter] = useState('');
    const [displaceReasonId, setDisplaceReasonId] = useState<number>(0);
    const [policeStationId, setPoliceStationId] = useState<number>(0);
    const [displaceModal, setDisplaceModal] = useState(false);
    const [canMapBeLoaded, setCanMapBeLoaded] = useState(false);
    const [selectedUnit, setSelectedUnit] = useState<UnitAndDistance>();
    const [mapCollapsed, setMapCollapsed] = useState(true);
    const loaders = useRef({ didUnitsFirstLoad: false });

    const [actingBodyUnitIds, setActingBodyUnitIds] = useState<number[]>([]);
    const [polygons, setPolygons] = useState<MapPolygon[]>([]);
    const [viewAllFilter, setViewAllFilter] = useState<boolean>(false);
    const [isStreetViewOpen, setIsStreetViewOpen] = useState(false);

    async function getUnits(unitsDispatched: Unit[], forceViewAllFilter: boolean = false) {
        try {
            if (!occurrence?.id) {
                return;
            }

            setIsLoading(true);
            const units = await dispatchService.getUnits({
                equipmentsIds: equipmentFilterList,
                skillsIds: skillFilterList,
                unitName: unitFilter,
                actingBodyId: occurrence.actingBodyId,
                actingBodyUnitIds: actingBodyUnitIds.length ? actingBodyUnitIds : undefined,
                dispatchedUnits: unitsDispatched.map(unit => unit.id),
                viewAll: forceViewAllFilter ? true : viewAllFilter
            });

            for (const unit of units) {
                (unit as UnitAndDistance).isStarter = false;
                (unit as UnitAndDistance).displaced = false;
            }

            setUnitsToDispatch(units);

            if (units.length === 0 && !viewAllFilter && !loaders.current.didUnitsFirstLoad) {
                setViewAllFilter(true);
                loaders.current.didUnitsFirstLoad = true;
                getUnits(unitsDispatched, true);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }

    async function getDispatchedUnits() {
        try {
            if (!occurrence?.id) {
                return;
            }
            const unitsDispatched = await dispatchService.getOccurrenceDispatchedUnits(occurrence.id);
            for (const unit of unitsDispatched) {
                const foundUnit = unit.OccurrenceUnits?.find(x => x.OccurrenceUnit?.occurrenceId == occurrence.id);
                (unit as UnitAndDistance).isStarter = foundUnit && foundUnit.OccurrenceUnit ? foundUnit.OccurrenceUnit.isStarter : false;
                (unit as UnitAndDistance).displaced = foundUnit && foundUnit.OccurrenceUnit ? foundUnit.OccurrenceUnit.displaced : false;
            }
            setDispatchedUnits(unitsDispatched);
            if (occurrence.situation !== 'concluded') {
                await getUnits(unitsDispatched);
            } else {
                setIsLoading(false);
            }
        } catch (err) {
            console.error(err);
        }
    }

    async function getAssignments() {
        try {
            const assignments = await dispatchService.getSimplifiedAssignments(occurrence.actingBodyId);
            setAssignments(assignments);
        } catch (err) {
            console.error(err);
        }
    }

    function toRadians(degrees: number) {
        return degrees * (Math.PI / 180);
    }

    function haversineDistance(location1: LatLng, location2: LatLng) {
        const deltaLatitude = toRadians(location2.lat - location1.lat);
        const deltaLongitude = toRadians(location2.lng - location1.lng);
        const haversineComponent = Math.sin(deltaLatitude / 2) ** 2 +
            Math.cos(toRadians(location1.lat)) * Math.cos(toRadians(location2.lat)) *
            Math.sin(deltaLongitude / 2) ** 2;
        const earthRadiusKm = 6371 * 2;
        const distance = earthRadiusKm * Math.asin(Math.sqrt(haversineComponent));
        return parseFloat(distance.toFixed(1));
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            getUnits(dispatchedUnits);
            setRefetchUser(!refetchUser);
        }
    }

    const renderStatus = (unit: UnitAndDistance) => {
        let customStyle: BadgeColor = 'green';
        let customName = 'available';
        let tooltip = 'active';

        if (unit.displaced) {
            customName = 'displace';
            tooltip = 'displaced';
            customStyle = 'blue';
        } else if (unit.definitiveStart && !unit.definitiveFinish && moment(unit.estimatedFinish).isBefore(moment())) {
            customName = 'femaleExceeded';
            tooltip = 'activeAfterSchedule';
            customStyle = 'red';
        } else if (unit.definitiveFinish) {
            customName = 'finished_f';
            tooltip = 'finished_f';
            customStyle = 'gray';
        } else if (unit.OccurrenceUnits && unit.OccurrenceUnits.length) {
            for (const occurrence of unit.OccurrenceUnits) {
                if (occurrence.OccurrenceUnit?.displaced) {
                    customName = 'displace';
                    tooltip = 'displacedInOtherOccurrence';
                    customStyle = 'purple';
                    break;
                }
            }
        }

        return <View style={{ justifyContent: 'center', alignItems: 'flex-start', zIndex: 1 }}>
            <Tooltip
                tooltipContent={<View>{translate(tooltip)}</View>}
                tooltipDirection='right'
            >
                <Badge
                    mode='SQUARE'
                    color={customStyle}
                >{translate(customName)}</Badge>
            </Tooltip>
        </View>;
    };

    const renderAttending = (unit: UnitAndDistance) => {
        if (unit.OccurrenceUnits && unit.OccurrenceUnits.length) {
            for (const occurrence of unit.OccurrenceUnits) {
                let title = '';
                if (occurrence.triggerType == 'face_detected') {
                    title = translate('faceDetectedAlert');
                } else if (occurrence.triggerType == 'mp_face_detected') {
                    title = translate('mpFaceDetectedAlert');
                } else if (['scenechangedetection', 'defocus'].includes(occurrence.triggerType || '')) {
                    title = translate('cameraDepredationSuspect');
                } else if (occurrence.triggerType == 'plate_detected') {
                    title = translate('plateDetectedAlert');
                } else if (occurrence.triggerType == 'mp_plate_detected') {
                    title = translate('mpPlateDetectedAlert');
                } else if (occurrence.triggerType == 'mp_mobile_agent_plate_detected') {
                    title = translate('mpMobileAgentPlateDetectedAlert');
                } else if (occurrence.triggerType == 'irregular_vehicle') {
                    title = translate('irregular_vehicle');
                } else if (occurrence.triggerType == 'clone_suspect') {
                    title = translate('clone_suspect');
                } else if (occurrence.triggerType == 'illegal_debris') {
                    title = translate('illegalDebrisAlert');
                } else if (occurrence.triggerType == 'area_invasion') {
                    title = translate('areaInvasionAlert');
                } else if (occurrence.triggerType == 'alarm_center') {
                    title = translate('alarmCenterAlert');
                } else if (occurrence.triggerType == 'guardian_app') {
                    title = translate('guardianAppAlert');
                } else if (occurrence.triggerType == 'sigrc') {
                    return translate('sigrcAlert');
                } else if (occurrence.triggerType == 'cortex_plate_detected') {
                    return translate('cortexPlateDetectedAlert');
                } else if (occurrence.triggerType == null && occurrence.occurrenceType) {
                    title = occurrence.occurrenceType.name;
                }
                return <MyAppText>{title}</MyAppText>;
            }
            return <MyAppText>{translate('yes')}</MyAppText>;
        }
        return <MyAppText>{translate('no')}</MyAppText>;
    };

    useEffect(() => {
        const occurrenceUnit = occurrence.OccurrenceUnits?.find(ou => ou.OccurrenceUnit.isStarter);
        setDisplaceReasonId(occurrenceUnit?.OccurrenceUnit.displaceReasonId || 0);
        setPoliceStationId(occurrenceUnit?.OccurrenceUnit.policeStationId || 0);

        getAssignments();
        getDispatchedUnits();
    }, []);

    const isFocused = useIsFocused();
    const isEnabled = isFocused && occurrence.situation !== 'concluded' && !!occurrence?.id;

    const occurrenceDetailAgentPositionsQuery = useQuery({
        queryKey: ['occurrenceAgentPositions', occurrence?.id, unitFilter, refetchUser],
        queryFn: () => dispatchService.getOccurrenceAgentPositions(occurrence.id!, unitFilter),
        enabled: isEnabled,
        refetchInterval: 30000,
    });

    const occurrenceDetailPoliceStationsQuery = useQuery({
        queryKey: ['occurrencePoliceStations', refetchUser],
        queryFn: () => dispatchService.getPoliceStationsPositions(),
        enabled: isEnabled,
        refetchInterval: 30000,
    });

    useEffect(() => {
        if (!occurrenceDetailAgentPositionsQuery.data) {
            return;
        }
        setAgentPositions(occurrenceDetailAgentPositionsQuery.data);
    }, [occurrenceDetailAgentPositionsQuery.data]);

    useEffect(() => {
        if (!occurrenceDetailPoliceStationsQuery.data) {
            return;
        }
        setPoliceStationsPositions(occurrenceDetailPoliceStationsQuery.data);
    }, [occurrenceDetailPoliceStationsQuery.data]);

    function updateUnitAgentPositions(pin: LatLng, units: UnitAndDistance[]) {
        for (const unit of units) {
            let distance = Infinity;
            if (unit.unitName && [UnitCategoryType.FOUR_WHEELS, UnitCategoryType.TWO_WHEELS].includes(unit.unitName.unitCategory.type)) {
                const agentPosition = agentPositions.find(ap => ap.user.unit?.id == unit.id);
                if (!agentPosition || moment(agentPosition.positionUpdatedAt).isBefore(moment().subtract(10, 'minutes'))) {
                    continue;
                }
                distance = haversineDistance(pin, {
                    lat: Number(agentPosition.latitude),
                    lng: Number(agentPosition.longitude),
                });
                for (const user of unit.users as UserAndDistances[]) {
                    user.distance = distance;
                }
            } else {
                for (const user of unit.users as UserAndDistances[]) {
                    const agentPosition = agentPositions.find(ap => ap.userId == user.id);
                    if (!agentPosition || moment(agentPosition.positionUpdatedAt).isBefore(moment().subtract(10, 'minutes'))) {
                        continue;
                    }
                    user.distance = haversineDistance(pin, {
                        lat: Number(agentPosition.latitude),
                        lng: Number(agentPosition.longitude),
                    });
                    if (user.distance < distance) {
                        distance = user.distance;
                    }
                }
            }
            unit.distance = distance;
        }
    }

    useEffect(() => {
        if (!pin || occurrence.situation == 'concluded') {
            return;
        }

        updateUnitAgentPositions(pin, dispatchedUnits as UnitAndDistance[]);
        updateUnitAgentPositions(pin, unitsToDispatch as UnitAndDistance[]);
        forceUpdate();
    }, [pin, dispatchedUnits, agentPositions, unitsToDispatch]);

    const checkDisabledStarter = useMemo(() => {
        if (occurrence.situation == 'concluded') {
            return true;
        }

        if ((dispatchedUnits as UnitAndDistance[]).findIndex(du => du.displaced) != -1) {
            return true;
        }
        return false;
    }, [dispatchedUnits, selectedUnit]);

    function userCreatedOccurrence(unit: UnitAndDistance & { odd?: boolean; }): boolean {
        return (occurrence.triggerType === 'plate_detected_by_mobile_agent'
            && occurrence.externalReference?.unitName != undefined
            && unit.unitName.name == occurrence.externalReference.unitName.name);
    }

    return (
        <>
            <View style={{ flex: 1, gap: 12, flexDirection: windowInfo.isMobile ? 'column' : 'row' }}>
                <View style={styles.leftArea(windowInfo.isMobile)}>
                    <View style={[styles.table, { rowGap: 32 }]}>
                        {occurrence.situation == 'concluded' ? <></> :
                            <View style={[styles.table, { gap: 8 }]}>
                                <View style={[styles.tableFilters, { minHeight: 40, justifyContent: 'space-between' }]}>
                                    <MyAppText style={styles.optionsButtonText}>
                                        {translate('availableUnits')}
                                    </MyAppText>
                                    <View style={{ flexDirection: 'row', gap: 16 }}>
                                        <FilterInput
                                            value={unitFilter}
                                            placeholder={translate('searchUnit')}
                                            onChange={setUnitFilter}
                                            onKeyPress={handleKeyDown}
                                        />
                                        <OccurrenceDetailFilter
                                            actingBodyId={occurrence.actingBodyId}
                                            equipmentFilterList={equipmentFilterList}
                                            setEquipmentFilterList={setEquipmentFilterList}
                                            skillFilterList={skillFilterList}
                                            setSkillFilterList={setSkillFilterList}
                                            textFilter={unitFilter}
                                            setTextFilter={setUnitFilter}
                                            actingBodyUnitIds={actingBodyUnitIds}
                                            handleFilter={() => {
                                                getUnits(dispatchedUnits);
                                                setRefetchUser(!refetchUser);
                                            }}
                                            setActingBodyUnitIds={setActingBodyUnitIds}
                                            setViewAllFilter={setViewAllFilter}
                                            viewAllFilter={viewAllFilter}
                                        />
                                    </View>
                                </View>
                                <View style={{
                                    zIndex: -1,
                                    flex: 1,
                                    borderColor: theme.colors.neutral[200],
                                    borderWidth: 1,
                                    borderRadius: 4,
                                    overflow: 'hidden',
                                }}>
                                    <MyDataTable
                                        rowHeight={40}
                                        useHighlightedHeader={true}
                                        columns={[
                                            {
                                                name: translate('status'),
                                                cell: row => renderStatus(row),
                                                maxWidth: '130px',
                                                minWidth: '130px',
                                            },
                                            {
                                                name: translate('team'),
                                                cell: row =>
                                                    <>
                                                        {userCreatedOccurrence(row)
                                                            ? <Tooltip tooltipDirection='right' tooltipContent={translate('occurrenceGeneratedByThisTeam')}>
                                                                <MyAppText>{row.unitName.name || ''}</MyAppText>
                                                            </Tooltip>
                                                            : <MyAppText>{row.unitName.name || ''}</MyAppText>
                                                        }
                                                    </>,
                                                wrap: true,
                                            },
                                            {
                                                name: translate('attending'),
                                                wrap: true,
                                                cell: row =>
                                                    <>
                                                        {renderAttending(row)}
                                                    </>,
                                            },
                                            {
                                                name: <FontAwesomeIcon icon={faBuilding} fontSize={16} color={theme.colors.iconColor} />,
                                                selector: row =>
                                                    `${row.actingBodyUnit?.actingBody?.name || ''}/${row.actingBodyUnit?.name || ''}`,
                                                wrap: true,
                                                center: true,
                                            },
                                            {
                                                center: true,
                                                name: <View style={{ flexDirection: 'row', columnGap: 5 }}>
                                                    <FontAwesomeIcon icon={faRoute} fontSize={16} color={theme.colors.iconColor} />
                                                    <MyAppText>(km)</MyAppText>
                                                </View>,
                                                selector: row => row.distance != Infinity && row.distance ? row.distance : row.distance == 0 ? translate('onSite') : '',
                                                sortable: true,
                                                width: '100px',
                                            },
                                            {
                                                button: true,
                                                cell: row => {
                                                    const idx = unitsToDispatch.findIndex((el) => el.id == row.id);

                                                    return <TouchableOpacity onPress={() => {
                                                        unitsToDispatch.splice(idx, 1);
                                                        if (!dispatchedUnits.length) {
                                                            row.isStarter = true;
                                                        }
                                                        dispatchedUnits.push(row);
                                                        setUnitsToDispatch([...unitsToDispatch]);
                                                        setDispatchedUnits([...dispatchedUnits]);
                                                    }} >
                                                        <Hoverable style={styles.button} >
                                                            {({ hovered }) => (
                                                                <FontAwesomeIcon fontSize={16} icon={faArrowDown} color={hovered ? theme.colors.tabs.selected : theme.colors.tabs.border} />
                                                            )}
                                                        </Hoverable>
                                                    </TouchableOpacity>;
                                                },
                                                width: '40px'
                                            }]}
                                        expandableRows
                                        onSelectedRowsChange={({ selectedRows }) => {
                                            if (selectedRows.length !== unitsToDispatch.length) {
                                                setUnitsToDispatch(selectedRows);
                                            }
                                        }}
                                        conditionalRowStyle={{
                                            when: row => userCreatedOccurrence(row),
                                            style: {
                                                backgroundColor: theme.colors.neutral['300']
                                            }
                                        }}
                                        expandableRowsComponent={({ data }) => <OccurrenceUnitContent unit={data} assignments={assignments} />}
                                        data={(unitsToDispatch as UnitAndDistance[]).sort((a, b) => {
                                            if (a.distance === undefined) {
                                                return 1;
                                            }
                                            if (b.distance === undefined) {
                                                return -1;
                                            }
                                            return a.distance - b.distance;
                                        })}
                                        progressPending={isLoading}
                                        shortPadding={true}
                                        tableFlex={true}
                                    />
                                </View>
                            </View>}
                        <View style={[styles.table, { gap: 8 }]}>
                            <View style={styles.tableFilters}>
                                <MyAppText style={styles.optionsButtonText}>
                                    {translate('dispatchedUnits')}
                                </MyAppText>
                            </View>
                            <View style={{
                                zIndex: -1,
                                flex: 1,
                                borderColor: theme.colors.neutral[200],
                                borderWidth: 1,
                                borderRadius: 4,
                                overflow: 'hidden',
                            }}>
                                <MyDataTable
                                    rowHeight={40}
                                    useHighlightedHeader={true}
                                    columns={[{
                                        name: translate('status'),
                                        cell: row => renderStatus(row),
                                        maxWidth: '130px',
                                        minWidth: '130px',
                                    }, {
                                        name: translate('team'),
                                        selector: row => row.unitName.name || '',
                                        wrap: true,
                                    },
                                    {
                                        name: '',
                                        cell: row => {
                                            return <MyCheckbox
                                                label={row.isStarter ? translate('starter') : translate('support')}
                                                checked={row.isStarter}
                                                setChecked={() => {
                                                    for (const unit of dispatchedUnits as UnitAndDistance[]) {
                                                        unit.isStarter = false;
                                                    }
                                                    row.isStarter = true;
                                                    forceUpdate();
                                                }}
                                                disabled={checkDisabledStarter}
                                            />;
                                        },
                                        wrap: true,
                                        center: true,
                                        maxWidth: '80px',
                                        minWidth: '80px'
                                    },
                                    {
                                        name: '',
                                        cell: row => {
                                            if (row.isStarter && !row.definitiveFinish) {
                                                return <TouchableOpacity onPress={async () => {
                                                    setDisplaceModal(true);
                                                    setSelectedUnit(row);
                                                }} style={[styles.displaceButton, row.displaced ? { minWidth: 115, maxWidth: 115 } : {}]}>
                                                    <MyAppText style={styles.text}>{translate(row.displaced ? 'displaced' : 'displace')}</MyAppText>
                                                </TouchableOpacity>;
                                            }
                                            return <></>;
                                        },
                                        wrap: true,
                                        center: true,
                                        maxWidth: '115px',
                                        minWidth: '115px',
                                        omit: occurrence.situation == 'concluded'
                                    },
                                    {
                                        name: translate('attending'),
                                        wrap: true,
                                        center: true,
                                        cell: row =>
                                            <>
                                                {renderAttending(row)}
                                            </>,
                                    },
                                    {
                                        name: <FontAwesomeIcon icon={faBuilding} fontSize={16} color={theme.colors.iconColor} />,
                                        selector: row =>
                                            `${row.actingBodyUnit?.actingBody?.name || ''}/${row.actingBodyUnit?.name || ''}`,
                                        wrap: true,
                                        center: true,
                                    },
                                    {
                                        center: true,
                                        name: <View style={{ flexDirection: 'row', columnGap: 5 }}>
                                            <FontAwesomeIcon icon={faRoute} fontSize={16} color={theme.colors.iconColor} />
                                            <MyAppText>(km)</MyAppText>
                                        </View>,
                                        selector: row => row.distance != Infinity && row.distance ? row.distance : row.distance == 0 ? translate('onSite') : '',
                                        sortable: true,
                                        width: '100px',
                                        omit: occurrence.situation == 'concluded'
                                    },
                                    {
                                        button: true,
                                        cell: row => {
                                            if (row.isStarter) {
                                                return <></>;
                                            }

                                            const idx = dispatchedUnits.findIndex((el) => el.id == row.id);

                                            return <TouchableOpacity onPress={() => {
                                                dispatchedUnits.splice(idx, 1);
                                                unitsToDispatch.push(row);
                                                setUnitsToDispatch([...unitsToDispatch]);
                                                setDispatchedUnits([...dispatchedUnits]);
                                            }} >

                                                <Hoverable style={styles.button} >
                                                    {({ hovered }) => (
                                                        <FontAwesomeIcon fontSize={16} icon={faArrowUp} color={hovered ? theme.colors.tabs.selected : theme.colors.tabs.border} />
                                                    )}
                                                </Hoverable>
                                            </TouchableOpacity>;
                                        },
                                        width: '40px',
                                        omit: occurrence.situation == 'concluded'
                                    }]}
                                    noResultsText={translate('noDispatchedUnits')}
                                    expandableRows
                                    onSelectedRowsChange={({ selectedRows }) => {
                                        if (selectedRows.length !== dispatchedUnits.length) {
                                            setDispatchedUnits(selectedRows);
                                        }
                                    }}
                                    expandableRowsComponent={({ data }) => <OccurrenceUnitContent unit={data} assignments={assignments} occurrenceSituation={occurrence.situation} />}
                                    data={(dispatchedUnits as UnitAndDistance[]).sort((a, b) => {
                                        if (a.distance === undefined) {
                                            return 1;
                                        }
                                        if (b.distance === undefined) {
                                            return -1;
                                        }
                                        return a.distance - b.distance;
                                    })}
                                    progressPending={isLoading}
                                    shortPadding={true}
                                    tableFlex={true}
                                />
                            </View>
                        </View>
                    </View>
                </View>
                <View style={[{
                    borderColor: theme.colors.neutral[200],
                    borderWidth: 1,
                    borderRadius: 8,
                    overflow: 'hidden',
                }, windowInfo.isMobile ? { height: 200 } : {
                    flex: 1,
                    maxWidth: mapCollapsed ? 215 : undefined,
                }]}>
                    <View style={{
                        minHeight: 40,
                        backgroundColor: theme.colors.neutral[400],
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}>
                        <MyAppText style={{
                            color: theme.colors.white[500],
                            fontSize: 16,
                            fontWeight: '600',
                        }}>{area ? titleCasePt(area) : ''}</MyAppText>
                    </View>
                    {occurrence.situation == 'concluded' && !canMapBeLoaded ? (
                        <TouchableOpacity
                            style={{
                                flex: 1,
                                justifyContent: 'center',
                                alignItems: 'center',
                                backgroundColor: theme.colors.neutral[100],
                            }}
                            onPress={() => setCanMapBeLoaded(true)}
                        >
                            <MyAppText style={{
                                color: theme.colors.neutral[500],
                                fontSize: 16,
                                fontWeight: 'bold', opacity: 1
                            }}>{translate('loadMap')}</MyAppText>
                        </TouchableOpacity>
                    ) : (
                        <>
                            <View style={{ flex: 1 }}>
                                {!isStreetViewOpen && (
                                    <View style={styles.overlaySelector}>
                                        <MapPolygonDropdown
                                            setPolygons={setPolygons}
                                            dropDownDirection={'BOTTOM'}
                                            zIndex={2}
                                        />
                                    </View>
                                )}
                                {!windowInfo.isMobileOrTablet &&
                                    <View pointerEvents="box-none" style={{ zIndex: 1, position: 'absolute', justifyContent: 'center', height: '100%' }}>
                                        <TouchableOpacity onPress={() => setMapCollapsed(!mapCollapsed)}>
                                            <View style={{
                                                width: 23,
                                                height: 94,
                                                backgroundColor: theme.colors.white[500],
                                                justifyContent: 'center',
                                                borderRightWidth: 1,
                                                borderTopWidth: 1,
                                                borderBottomWidth: 1,
                                                borderColor: theme.colors.neutral[200],
                                                borderBottomEndRadius: 16,
                                                borderTopEndRadius: 16,
                                            }}>
                                                <FontAwesomeIcon icon={(mapCollapsed ? faChevronLeft : faChevronRight) as IconDefinition} color={theme.colors.neutral[400]} />
                                            </View>
                                        </TouchableOpacity>
                                    </View>
                                }
                                <Map
                                    dynamicMarker={pin}
                                    useDynamicMarker={true}
                                    agentPositions={agentPositions}
                                    draggable={occurrence.cityPropertyId ? false : true}
                                    policeStations={policeStationsPositions}
                                    polygons={polygons}
                                    markerChanged={markerChanged}
                                    onStreetViewChanged={setIsStreetViewOpen}
                                />
                            </View>
                        </>
                    )}
                </View>
            </View>
            {selectedUnit && <DisplacedModal
                isModalVisible={displaceModal}
                setIsModalVisible={setDisplaceModal}
                setDisplaceReasonId={setDisplaceReasonId}
                policeStationId={policeStationId}
                setPoliceStationId={setPoliceStationId}
                displaceReasonId={displaceReasonId}
                unit={selectedUnit}
                onSubmit={() => {
                    setDisplaceModal(false);
                    if (selectedUnit) {
                        const unit = dispatchedUnits.find(u => u.id == selectedUnit.id);
                        if (!unit) {
                            return;
                        }

                        (unit as UnitAndDistance).displaced = true;
                        (unit as UnitAndDistance).displaceReasonId = displaceReasonId;
                        (unit as UnitAndDistance).policeStationId = policeStationId;

                        setDispatchedUnits([...dispatchedUnits]);
                    }
                }}
                isDisplaced={!!(selectedUnit && selectedUnit.displaced)}
                onRemoveDisplaced={() => {
                    if (selectedUnit) {
                        const copy = [];
                        for (const du of dispatchedUnits) {
                            (du as UnitAndDistance).displaced = false;
                            (du as UnitAndDistance).displaceReasonId = undefined;
                            copy.push(du);
                        }
                        setDisplaceReasonId(0);
                        setPoliceStationId(0);
                        setDispatchedUnits(copy);
                        setDisplaceModal(false);
                    }
                }}
            />}
        </>);
}

const styleSheet = createStyleSheet((theme) => ({
    tableFilters: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap'
    },
    leftArea: (isMobile: boolean) => ({
        flex: !isMobile ? 1 : undefined,
        flexDirection: 'column',
        flexGrow: !isMobile ? 2 : undefined,
        height: isMobile ? 600 : undefined,
    }),
    table: {
        flex: 1,
    },
    optionsButtonText: {
        color: theme.colors.labelColor,
        fontSize: 16,
        fontWeight: '600',
    },
    button: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 25
    },
    displaceButton: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: theme.colors.primaryButton.background,
        borderRadius: 2,
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        gap: 5,
        padding: 5,
        minWidth: 95,
        maxWidth: 95,
        maxHeight: 35
    },
    text: {
        color: theme.colors.primaryButton.text,
        fontSize: 12,
    },
    overlaySelector: {
        position: 'absolute',
        zIndex: 1000,
        width: '100%',
        paddingHorizontal: 8,
        paddingVertical: 8,
    },
}));
